import { X } from "@phosphor-icons/react";
import { forwardRef, type MouseEvent } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/cn";
import type { ChipProps } from "./Chip.types";

const chipVariants = cva("bfrs-inline-flex bfrs-min-w-0 bfrs-items-center bfrs-rounded-[var(--bfrs-chip-radius)] bfrs-font-medium bfrs-outline-none bfrs-transition", {
  variants: {
    size: {
      xs: "bfrs-gap-[5px] bfrs-rounded-[100px] bfrs-px-[6px] bfrs-py-[2px] bfrs-text-[10px] bfrs-font-normal",
      sm: "bfrs-min-h-6 bfrs-gap-1 bfrs-px-2 bfrs-text-xs",
      md: "bfrs-min-h-7 bfrs-gap-1.5 bfrs-px-2.5 bfrs-text-[12.5px]",
      lg: "bfrs-min-h-8 bfrs-gap-2 bfrs-px-3 bfrs-text-sm"
    },
    variant: {
      default: "",
      soft: "",
      outline: "bfrs-border bfrs-bg-transparent",
      filled: ""
    },
    tone: {
      neutral: "",
      primary: "",
      brand: "",
      success: "",
      warning: "",
      danger: "",
      info: ""
    },
    selected: {
      true: "bfrs-ring-1 bfrs-ring-primary/30"
    },
    clickable: {
      true: "bfrs-cursor-pointer focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus"
    }
  },
  compoundVariants: [
    { variant: "default", tone: "neutral", className: "bfrs-border-border bfrs-bg-surface-app bfrs-text-text-secondary" },
    { variant: "default", tone: "primary", className: "bfrs-border-primary-border bfrs-bg-primary-soft bfrs-text-primary" },
    { variant: "default", tone: "brand", className: "bfrs-border-primary-border bfrs-bg-primary-soft bfrs-text-primary" },
    { variant: "default", tone: "success", className: "bfrs-border-success-soft bfrs-bg-success-soft bfrs-text-success-contrast" },
    { variant: "default", tone: "warning", className: "bfrs-border-warning-soft bfrs-bg-warning-soft bfrs-text-warning-contrast" },
    { variant: "default", tone: "danger", className: "bfrs-border-danger-soft bfrs-bg-danger-soft bfrs-text-danger-contrast" },
    { variant: "default", tone: "info", className: "bfrs-border-info-soft bfrs-bg-info-soft bfrs-text-info-contrast" },
    { variant: "soft", tone: "neutral", className: "bfrs-border-border bfrs-bg-surface-app bfrs-text-text-secondary" },
    { variant: "soft", tone: "primary", className: "bfrs-border-primary-border bfrs-bg-primary-soft bfrs-text-primary" },
    { variant: "soft", tone: "brand", className: "bfrs-border-primary-border bfrs-bg-primary-soft bfrs-text-primary" },
    { variant: "soft", tone: "success", className: "bfrs-border-success-soft bfrs-bg-success-soft bfrs-text-success-contrast" },
    { variant: "soft", tone: "warning", className: "bfrs-border-warning-soft bfrs-bg-warning-soft bfrs-text-warning-contrast" },
    { variant: "soft", tone: "danger", className: "bfrs-border-danger-soft bfrs-bg-danger-soft bfrs-text-danger-contrast" },
    { variant: "soft", tone: "info", className: "bfrs-border-info-soft bfrs-bg-info-soft bfrs-text-info-contrast" },
    { variant: "outline", tone: "neutral", className: "bfrs-border-border-strong bfrs-text-text-secondary" },
    { variant: "outline", tone: "primary", className: "bfrs-border-primary bfrs-text-primary" },
    { variant: "outline", tone: "brand", className: "bfrs-border-primary bfrs-text-primary" },
    { variant: "outline", tone: "success", className: "bfrs-border-success bfrs-text-success" },
    { variant: "outline", tone: "warning", className: "bfrs-border-warning bfrs-text-warning" },
    { variant: "outline", tone: "danger", className: "bfrs-border-danger bfrs-text-danger" },
    { variant: "outline", tone: "info", className: "bfrs-border-info bfrs-text-info" },
    { variant: "filled", tone: "neutral", className: "bfrs-border-text-secondary bfrs-bg-text-secondary bfrs-text-text-inverse" },
    { variant: "filled", tone: "primary", className: "bfrs-border-primary bfrs-bg-primary bfrs-text-primary-contrast" },
    { variant: "filled", tone: "brand", className: "bfrs-border-primary bfrs-bg-primary bfrs-text-primary-contrast" },
    { variant: "filled", tone: "success", className: "bfrs-border-success bfrs-bg-success bfrs-text-primary-contrast" },
    { variant: "filled", tone: "warning", className: "bfrs-border-warning bfrs-bg-warning bfrs-text-warning-contrast" },
    { variant: "filled", tone: "danger", className: "bfrs-border-danger bfrs-bg-danger bfrs-text-danger-contrast" },
    { variant: "filled", tone: "info", className: "bfrs-border-info bfrs-bg-info bfrs-text-primary-contrast" }
  ],
  defaultVariants: {
    size: "sm",
    variant: "default",
    tone: "neutral"
  }
});

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, variant = "default", tone = "neutral", size = "sm", leftIcon, rightIcon, selected, removable, onRemove, removeLabel = "Remove", children, onClick, ...props }, ref) => {
    const showRemove = Boolean(removable || onRemove);

    return (
      <span
        ref={ref}
        role={onClick ? "button" : props.role}
        tabIndex={onClick ? 0 : props.tabIndex}
        className={cn(chipVariants({ variant, tone, size, selected, clickable: Boolean(onClick) }), className)}
        onClick={onClick}
        onKeyDown={(event) => {
          props.onKeyDown?.(event);
          if (onClick && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onClick(event as unknown as MouseEvent<HTMLSpanElement>);
          }
        }}
        {...props}
      >
        {leftIcon}
        <span className="bfrs-truncate">{children}</span>
        {rightIcon}
        {showRemove && (
          <button
            type="button"
            aria-label={removeLabel}
            disabled={!onRemove}
            className="bfrs-ml-0.5 bfrs-rounded-sm bfrs-p-0.5 bfrs-text-text-muted bfrs-outline-none hover:bfrs-bg-surface-card/70 hover:bfrs-text-text-primary focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-50"
            onClick={(event) => {
              event.stopPropagation();
              onRemove?.(event);
            }}
          >
            <X size={12} weight="bold" />
          </button>
        )}
      </span>
    );
  }
);

Chip.displayName = "Chip";
